import {
  EnvironmentInjector,
  inject,
  Injectable,
  runInInjectionContext,
} from '@angular/core';
import {
  and,
  arrayRemove,
  arrayUnion,
  docData,
  getDoc,
  getDocs,
  limit,
  or,
  query,
  runTransaction,
  where,
} from '@angular/fire/firestore';
import { Club } from '@models';
import { Season } from '@models';
import { Team } from '@models';
import { convertTimestampsToDates } from '@utils';
import {
  catchError,
  combineLatest,
  defer,
  firstValueFrom,
  map,
  Observable,
  of,
  ReplaySubject,
  share,
  shareReplay,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';
import { AuthService } from './auth.service';
import { ClubsService } from './club.service';
import { FirestoreService } from './firestore.service';
import { SeasonManagerService } from './season-manager.service';
import { SeasonsService } from './seasons.service';
import { SportContextService } from './sport-context.service';
import { TenantContextService } from './tenant-context.service';

/**
 * Stable codes of the rejections `TeamsService` raises on its own.
 *
 * They are *codes*, not `instanceof` checks, on purpose: the value survives
 * bundling, duplicated class identities and re-throws, exactly like the
 * callable error codes in `@utils/callable-errors`.
 *
 * Every code here **must** have a translated message in the UI that catches it
 * (`teams.form.*` in `apps/admin/src/assets/i18n/es.json`): `parseBackendError`
 * prints `error.message` straight into the toast, so an untranslated `Error`
 * thrown from this service becomes English technical copy on screen.
 */
export const TEAM_SEASON_CHANGE_BLOCKED_BY_MATCHES_CODE =
  'team/season-change-blocked-by-matches';
export const TEAM_SEASON_CHANGE_CONFLICT_CODE = 'team/season-change-conflict';
export const TEAM_NOT_FOUND_CODE = 'team/not-found';
export const TEAM_READ_FAILED_CODE = 'team/read-failed';

/**
 * Rejection raised by `TeamsService` itself (as opposed to one bubbling up
 * from the Firestore SDK). `message` is developer-facing English for the
 * console; the UI must translate `code`, never print `message`.
 */
export class TeamServiceError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    options?: { cause?: unknown },
  ) {
    super(message);
    this.name = 'TeamServiceError';
    if (options && 'cause' in options) {
      (this as { cause?: unknown }).cause = options.cause;
    }
  }
}

/**
 * Moving a team to another season would orphan everything that already points
 * at it inside the old one: the league fixture list, the cup bracket stored in
 * `season.groups` and every `match` holding its id in
 * `localTeamId`/`visitorTeamId`. Product decision: **do not repair, refuse**.
 *
 * The whole save is rejected (the team document is not written either), so the
 * user gets a single, unambiguous outcome: delete the fixture list first, then
 * change the season.
 */
export class TeamSeasonChangeBlockedByMatchesError extends TeamServiceError {
  constructor(
    public readonly teamId: string,
    public readonly seasonId: string,
  ) {
    super(
      TEAM_SEASON_CHANGE_BLOCKED_BY_MATCHES_CODE,
      `Team "${teamId}" cannot change season: it already has matches in season "${seasonId}".`,
    );
    this.name = 'TeamSeasonChangeBlockedByMatchesError';
  }
}

/**
 * Code of a rejection raised by this service, or `''` for anything else
 * (Firestore SDK errors, callable errors, plain `Error`s...). Callers use it to
 * pick the translated message to show.
 */
export function getTeamServiceErrorCode(error: unknown): string {
  const code = (error as { code?: unknown } | null | undefined)?.code;
  return typeof code === 'string' && code.startsWith('team/') ? code : '';
}

/**
 * How long a by-id read of a team outside `all$` keeps its listener after the
 * last subscriber leaves (see `TeamsService.getOutsideSelection`).
 */
const OUTSIDE_SELECTION_KEEPALIVE_MS = 30_000;

type EnrichedTeam = Team & { club?: Club; season?: Season };

@Injectable({
  providedIn: 'root',
})
export class TeamsService {
  private teams$!: Observable<Team[]>;
  public all$!: Observable<EnrichedTeam[]>;
  public clubsWithTeams$!: Observable<(Club & { teams: Team[] })[]>;

  private readonly injector = inject(EnvironmentInjector);
  private readonly authService = inject(AuthService);
  /**
   * By-id reads of teams that are not in `all$`, keyed by document path (the
   * path carries the tenant). See `getOutsideSelection()`.
   */
  private readonly outsideSelection = new Map<
    string,
    Observable<EnrichedTeam | undefined>
  >();

  constructor(
    private firestore: FirestoreService,
    private clubsService: ClubsService,
    private seasonsService: SeasonsService,
    private seasonManager: SeasonManagerService,
    private tenantContext: TenantContextService,
    private sportContext: SportContextService,
  ) {
    this.teams$ = combineLatest([
      this.tenantContext.tenantId$,
      this.seasonManager.season$,
      this.sportContext.activeSport$,
      this.sportContext.isMultiSport$,
    ]).pipe(
      switchMap(([, seasonYear, activeSport, isMultiSport]) => {
        const constraints = [where('seasonYear', '==', seasonYear)];
        if (isMultiSport && activeSport) {
          constraints.push(where('sport', '==', activeSport));
        }
        return this.firestore
          .listDocuments<Team>(
            this.tenantContext.collections.TEAMS,
            ...constraints,
          )
          .pipe(shareReplay({ bufferSize: 1, refCount: true }));
      }),
    );

    this.all$ = this.seasonManager.season$.pipe(
      switchMap((seasonYear) =>
        combineLatest([
          this.teams$.pipe(
            map((teams) =>
              teams.filter((team) => team.seasonYear === seasonYear),
            ),
          ),
          this.clubsService.all$,
          this.seasonsService.all$,
        ]),
      ),
      map(([teams, clubs, seasons]) =>
        teams.map((team) => ({
          ...team,
          club: clubs.find((c) => c.id === team.clubId),
          season: seasons.find((s) => s.id === team.seasonId),
        })),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.clubsWithTeams$ = combineLatest([
      this.clubsService.all$,
      this.teams$,
    ]).pipe(
      map(([clubs, teams]) =>
        clubs.map((club) => ({
          ...club,
          teams: teams.filter((team) => team.clubId === club.id),
        })),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private get col() {
    return this.tenantContext.collections.TEAMS;
  }

  /**
   * Team by id (enriched with `club` and `season`), whatever the selected
   * season year.
   *
   * Looks it up in `all$` first (same object and same synchronous emission as
   * before). When it is not there — typically because it belongs to another
   * `seasonYear` — it falls back to reading the document by id and applies the
   * same enrichment.
   *
   * Emits `undefined` (despite the historical non-optional type) when the
   * document does not exist, is soft-deleted, cannot be read, or belongs to
   * another sport in a multi-sport tenant (the sport filter of `all$` is kept;
   * only the year filter is dropped).
   */
  get(id: string): Observable<EnrichedTeam> {
    return this.all$.pipe(
      switchMap((teams) => {
        const found = teams.find((t) => t.id === id);
        return found ? of(found) : this.getOutsideSelection(id);
      }),
    ) as Observable<EnrichedTeam>;
  }

  /**
   * Live by-id read used by `get()` when the team is not in `all$`.
   *
   * - `docData` directly (not `FirestoreService.getDocument`, which never emits
   *   for a missing document and would hang every `firstValueFrom(get(id))`).
   * - Same shape as `all$`: dates converted, `deleted` docs dropped, same sport
   *   filter, `club` from `ClubsService.all$` and `season` from
   *   `SeasonsService.get()` (which itself resolves seasons of any year).
   * - Cached per path and shared with a delayed reset: the impure `team` pipe
   *   re-subscribes on every change detection, so the stream must survive a
   *   momentary zero ref-count and replay synchronously, instead of opening a
   *   new listener each cycle.
   */
  private getOutsideSelection(id: string): Observable<EnrichedTeam | undefined> {
    const path = this.col;
    const key = `${path}/${id}`;
    let cached = this.outsideSelection.get(key);
    if (!cached) {
      const doc$ = defer(() =>
        runInInjectionContext(this.injector, () =>
          docData<Team>(this.firestore.getDocumentRef<Team>(path, id), {
            idField: 'id',
          }),
        ),
      ).pipe(
        catchError((err) => {
          console.error(`[TeamsService] get(${id}) error:`, err);
          return of(undefined);
        }),
      );
      cached = combineLatest([
        doc$,
        this.sportContext.activeSport$,
        this.sportContext.isMultiSport$,
      ]).pipe(
        map(([team, activeSport, isMultiSport]) => {
          if (!team || team.deleted === true) return undefined;
          if (isMultiSport && activeSport && team.sport !== activeSport) {
            return undefined;
          }
          return convertTimestampsToDates(team);
        }),
        switchMap((team) =>
          team
            ? combineLatest([
                this.clubsService.all$,
                team.seasonId
                  ? this.seasonsService.get(team.seasonId)
                  : of(undefined),
              ]).pipe(
                map(
                  ([clubs, season]): EnrichedTeam => ({
                    ...team,
                    club: clubs.find((c) => c.id === team.clubId),
                    season: season ?? undefined,
                  }),
                ),
              )
            : of(undefined),
        ),
        takeUntil(this.authService.onLogout),
        share({
          connector: () => new ReplaySubject<EnrichedTeam | undefined>(1),
          resetOnError: true,
          resetOnComplete: true,
          resetOnRefCountZero: () => timer(OUTSIDE_SELECTION_KEEPALIVE_MS),
        }),
      );
      this.outsideSelection.set(key, cached);
    }
    return cached;
  }

  findOne(teamId: string) {
    return firstValueFrom(this.firestore.getDocument<Team>(this.col, teamId));
  }

  getByClub(clubId: string) {
    return this.all$.pipe(
      map((teams) => teams.filter((t) => t.clubId === clubId)),
    );
  }

  getByScope(scope: string) {
    return this.all$.pipe(
      map((teams) => teams.filter((t) => t.scope === scope)),
    );
  }

  create(team: Omit<Team, 'id'>) {
    return this.firestore.create<Team>(this.col, team);
  }

  update(id: string, team: Partial<Team>) {
    return this.firestore.update<Team>(this.col, { id, ...team });
  }

  async createOrUpdate(id: string | undefined, team: Omit<Team, 'id'>) {
    const t = team as Team;
    const season = await firstValueFrom(this.seasonsService.get(t.seasonId));
    t.scope = season.scope;
    t.seasonYear = this.seasonManager.getActualSeason();
    if (!t.sport) {
      t.sport = this.sportContext.getActiveSport() ?? undefined;
    }
    if (!id) {
      // Creating a team never touches `season.teamIds`: a team only enters the
      // competition list once it is validated (see `validateTeam`).
      return this.create(t);
    }
    const persistedSeasonId = await this.getPersistedSeasonId(id);
    if (!persistedSeasonId || persistedSeasonId === t.seasonId) {
      // Season untouched (the overwhelmingly common edit): zero extra queries,
      // zero writes on season documents. Same behaviour as before the fix.
      return this.update(id, t);
    }
    // Season change requested. It is only allowed while the team has no
    // matches generated in the season it is leaving: otherwise the fixture
    // list, the cup bracket and the matches themselves would keep pointing at
    // the old season with no way to tell. Reject the *whole* save, do not
    // repair anything.
    if (await this.hasMatchesInSeason(id, persistedSeasonId)) {
      throw new TeamSeasonChangeBlockedByMatchesError(id, persistedSeasonId);
    }
    return this.updateMovingSeason(id, t, persistedSeasonId);
  }

  /**
   * `true` when at least one match of `seasonId` references `teamId` as local
   * or visitor team, whatever its status.
   *
   * Same query shape as `MatchesService.getFinishedMatchesByTeamAndSeasonId()`
   * but **without the `status` filter**: a pending, postponed or suspended
   * match is just as generated as a finished one, and moving the team would
   * orphan it all the same. It is also a one-shot `getDocs` with `limit(1)`
   * (existence check, not a count) instead of a live `collectionData`
   * subscription, which would never complete and would hang the caller.
   *
   * Errors are **not** swallowed: if the existence of matches cannot be
   * established, the season change must not go ahead (fail closed). The UI
   * pre-check does catch them, since there the fallback is simply to leave the
   * season select enabled and let this guard decide at save time.
   */
  async hasMatchesInSeason(teamId: string, seasonId: string): Promise<boolean> {
    const matchesCol = this.firestore.getCollectionRef(
      this.tenantContext.collections.MATCHES,
    );
    const matchesQuery = query(
      matchesCol,
      and(
        or(
          where('localTeamId', '==', teamId),
          where('visitorTeamId', '==', teamId),
        ),
        where('seasonId', '==', seasonId),
      ),
      limit(1),
    );
    const snapshot = await getDocs(matchesQuery);
    return !snapshot.empty;
  }

  /**
   * One-shot, cache-free read of the `seasonId` currently stored in Firestore.
   * `undefined` means **the document does not exist**, nothing else.
   *
   * Deliberately NOT `get(id)` (it resolves against `all$`, filtered by the
   * active season year and sport, so it can miss the team) and NOT `findOne()`
   * (`getDocument` uses `docData` and swallows `undefined`, so a missing doc
   * would never emit and the caller would hang forever).
   *
   * A **read failure is rethrown, never turned into `undefined`**. The caller
   * treats `undefined` as "no previous season" and takes the fast path, so
   * swallowing the error would save the new `seasonId` without checking for
   * matches and without syncing `season.teamIds` — the exact fail-open this
   * guard exists to prevent. If the current state cannot be read, the save is
   * rejected (and a client that cannot read the team document could not have
   * written it either).
   */
  private async getPersistedSeasonId(
    teamId: string,
  ): Promise<string | undefined> {
    let snap;
    try {
      snap = await getDoc(
        this.firestore.getDocumentRef<Team>(this.col, teamId),
      );
    } catch (err) {
      console.error('[TeamsService] getPersistedSeasonId error:', err);
      throw new TeamServiceError(
        TEAM_READ_FAILED_CODE,
        `Could not read team "${teamId}" before saving.`,
        { cause: err },
      );
    }
    return snap.exists() ? (snap.data() as Team).seasonId : undefined;
  }

  /**
   * Updates a team whose `seasonId` changed, keeping `season.teamIds` in sync.
   *
   * `season.teamIds` holds the teams *enrolled* in that season's competition,
   * and a team is enrolled when it gets validated (`validateTeam` does the
   * `arrayUnion`). Moving a team to another season must therefore:
   *   - `arrayRemove` it from the previous season, always;
   *   - `arrayUnion` it into the new season only when the team is validated.
   *
   * Everything runs inside a transaction so the team document and both season
   * documents move together: the team can never end up listed in two seasons
   * at once, nor be silently dropped from both.
   *
   * The previous `seasonId` is re-read inside the transaction (the value used
   * by the caller for the fast-path check may be stale). A previous season
   * that no longer exists is skipped: there is nothing to clean up and the
   * team save must not fail because of it.
   *
   * `checkedSeasonId` is the season the caller already verified to be free of
   * matches (`hasMatchesInSeason`). A Firestore transaction can only `get()`
   * document references — it cannot run the `or()` query the guard needs — so
   * the check necessarily happens *before* the transaction. If the re-read
   * shows the team is now leaving a *different* season, that season was never
   * checked: the transaction aborts instead of moving the team blindly.
   */
  private async updateMovingSeason(
    id: string,
    team: Team,
    checkedSeasonId: string,
  ) {
    const seasonsCol = this.tenantContext.collections.SEASONS;
    const teamRef = this.firestore.getDocumentRef<Team>(this.col, id);

    return runTransaction(this.firestore.db, async (transaction) => {
      // --- reads (Firestore requires every read before any write) ---
      const teamDoc = await transaction.get(teamRef as any);
      if (!teamDoc.exists()) {
        throw new TeamServiceError(
          TEAM_NOT_FOUND_CODE,
          `Team "${id}" does not exist`,
        );
      }
      const persisted = teamDoc.data() as Team;
      const previousSeasonId = persisted.seasonId;
      // The edit form does not submit `validated`, so fall back to the stored
      // value instead of dropping a validated team out of every season list.
      const validated = team.validated ?? persisted.validated;

      const seasonChanged =
        !!previousSeasonId && previousSeasonId !== team.seasonId;

      if (seasonChanged && previousSeasonId !== checkedSeasonId) {
        // Someone moved the team between the guard and this transaction. The
        // season it is leaving now was never checked for matches, and the
        // check cannot be redone here (no queries inside a transaction), so
        // refuse rather than risk orphaning a fixture list. Retrying the save
        // re-runs the guard against the fresh season.
        throw new TeamServiceError(
          TEAM_SEASON_CHANGE_CONFLICT_CODE,
          `Team "${id}" changed season concurrently (expected "${checkedSeasonId}", found "${previousSeasonId}"). Retry the save.`,
        );
      }

      const previousSeasonRef = seasonChanged
        ? this.firestore.getDocumentRef<Season>(seasonsCol, previousSeasonId)
        : undefined;
      const previousSeasonDoc = previousSeasonRef
        ? await transaction.get(previousSeasonRef as any)
        : undefined;

      const nextSeasonRef =
        seasonChanged && validated === true
          ? this.firestore.getDocumentRef<Season>(seasonsCol, team.seasonId)
          : undefined;
      const nextSeasonDoc = nextSeasonRef
        ? await transaction.get(nextSeasonRef as any)
        : undefined;

      // --- writes ---
      const now = new Date();
      transaction.update(teamRef as any, { ...team, id, updatedAt: now });

      if (previousSeasonRef && previousSeasonDoc?.exists()) {
        transaction.update(previousSeasonRef as any, {
          teamIds: arrayRemove(id),
          updatedAt: now,
        });
      }
      if (nextSeasonRef && nextSeasonDoc?.exists()) {
        transaction.update(nextSeasonRef as any, {
          teamIds: arrayUnion(id),
          updatedAt: now,
        });
      }
    });
  }

  deleteForever(teamId: string) {
    return this.firestore.deleteForever(this.col, teamId);
  }

  addDelegate(teamId: string, userId: string) {
    return this.firestore.update<Team>(this.col, {
      id: teamId,
      delegatesIds: arrayUnion(userId) as any,
    });
  }

  removeDelegate(teamId: string, userId: string) {
    return this.firestore.update<Team>(this.col, {
      id: teamId,
      delegatesIds: arrayRemove(userId) as any,
    });
  }

  async changeTeamPlayer(userId: string, fromTeamId: string, toTeamId: string) {
    return runTransaction(this.firestore.db, async (transaction) => {
      const fromRef = this.firestore.getDocumentRef<Team>(this.col, fromTeamId);
      const toRef = this.firestore.getDocumentRef<Team>(this.col, toTeamId);
      const [fromDoc, toDoc] = await Promise.all([
        transaction.get(fromRef as any),
        transaction.get(toRef as any),
      ]);
      if (!fromDoc.exists() || !toDoc.exists()) {
        throw new Error('One or both teams do not exist');
      }
      const fromData = fromDoc.data() as Team;
      if (!fromData.playersIds?.includes(userId)) {
        throw new Error('Player is not in the source team');
      }
      transaction.update(fromRef as any, { playersIds: arrayRemove(userId) });
      transaction.update(toRef as any, { playersIds: arrayUnion(userId) });
    });
  }

  getByClubAndScope(clubId: string, scope: string): Observable<Team[]> {
    return this.all$.pipe(
      map((teams) =>
        teams.filter(
          (team) => team.clubId === clubId && scope === (team as any).scope,
        ),
      ),
    );
  }

  deleteDelegate(teamId: string, userId: string) {
    return this.firestore.update<Team>(this.col, {
      id: teamId,
      delegatesIds: arrayRemove(userId),
    } as any);
  }
}
